import type {
  ConsoleEntry,
  ConsoleLevel,
  NetworkEntry,
  NetworkEntryDetail,
  NetworkMethod,
  NetworkType,
  ErrorEntry,
  BrowserInfo,
  LogData,
} from '@/modules/viewer/types/logs';
import type { RawInsights, RawConsoleEntry, RawErrorEntry, RawHarEntry, RawHarHeaders, RawBrowserInfoEntry } from './insightsRawTypes';

/**
 * Pure transformers that map raw SDK output (insights.zip contents) into the
 * frontend's `LogData` shape. No DOM, no Pinia, no fetch — fully unit-testable.
 *
 * Time anchor: every entry's `timestamp` is `(absoluteEpochMs - anchorMs) / 1000`,
 * clamped to 0 via `Math.max(0, …)` to defend against clock skew. The anchor
 * is the `sessionStartMs` the extractor parses out of the SDK filename prefix
 * (`session-{sessionStartMs}-{id}-…`) — i.e. the exact instant the recording
 * began. When it's missing, the caller passes `Number.MAX_SAFE_INTEGER` as a
 * sentinel so every subtraction underflows and the clamp collapses every
 * entry to 0 — content still renders, only per-entry offsets are lost.
 */

export const normalize = (raw: RawInsights, anchorMs: number): LogData => ({
  console: normalizeConsoleEntries(raw.console, anchorMs),
  network: normalizeNetworkEntries(raw.network.log.entries, anchorMs),
  errors: normalizeErrorEntries(raw.errors, anchorMs),
  browser: normalizeBrowserInfo(raw.browserInfo[0] ?? {}),
});

// ---------------------------------------------------------------------------
// Console
// ---------------------------------------------------------------------------

export const normalizeConsoleEntries = (raw: RawConsoleEntry[], anchorMs: number): ConsoleEntry[] =>
  raw.map((entry, i) => ({
    id: `console-${i}`,
    level: entry.type as ConsoleLevel,
    timestamp: relativeSeconds(entry.time, anchorMs),
    message: formatConsoleMessage(entry.message),
  }));

/**
 * Format a `console.log(arg1, arg2, ...)` call's variadic args into a single
 * displayable string. Strings pass through; objects get JSON.stringify'd with
 * 2-space indent. Joined by space.
 */
export const formatConsoleMessage = (parts: unknown[]): string => {
  if (!Array.isArray(parts)) {
    return formatConsolePart(parts);
  }
  return parts.map(formatConsolePart).join(' ');
};

const formatConsolePart = (part: unknown): string => {
  if (typeof part === 'string') return part;
  if (part === null) return 'null';
  if (part === undefined) return 'undefined';
  try {
    return JSON.stringify(part, null, 2);
  } catch {
    return String(part);
  }
};

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

export const normalizeErrorEntries = (raw: RawErrorEntry[], anchorMs: number): ErrorEntry[] =>
  raw.map((entry, i) => {
    const timestamp = relativeSeconds(entry.time, anchorMs);
    if (entry.type === 'unhandledrejection') {
      return {
        id: `error-${i}`,
        timestamp,
        message: `Unhandled rejection: ${entry.message.reason}`,
        // SDK doesn't capture a real stack trace yet — `error: {}` is always empty.
        stack: '',
      };
    }
    // type === 'error'
    return {
      id: `error-${i}`,
      timestamp,
      message: entry.message.message,
      stack: '',
      url: entry.message.source,
      line: entry.message.lineno,
    };
  });

// ---------------------------------------------------------------------------
// Network
// ---------------------------------------------------------------------------

export const normalizeNetworkEntries = (raw: RawHarEntry[], anchorMs: number): NetworkEntry[] =>
  raw.map((entry, i) => ({
    id: `network-${i}`,
    timestamp: relativeSeconds(Date.parse(entry.startedDateTime), anchorMs),
    method: (entry.request.method ?? 'GET') as NetworkMethod,
    url: entry.request.url,
    status: entry.response.status,
    duration: Math.round(entry.time),
    type: classifyNetworkType(entry.timings?.initiatorType),
    detail: normalizeNetworkDetail(entry),
  }));

/**
 * Classify a HAR entry by its initiator type. The HAR spec doesn't carry
 * "xhr vs asset" — we derive it from the SDK-supplied `initiatorType` field
 * (which mirrors PerformanceResourceTiming.initiatorType).
 */
export const classifyNetworkType = (initiatorType: string | undefined): NetworkType => {
  if (initiatorType === 'xmlhttprequest' || initiatorType === 'fetch') {
    return 'xhr';
  }
  return 'asset';
};

export const normalizeNetworkDetail = (entry: RawHarEntry): NetworkEntryDetail => {
  const reqHeaders = normalizeHeaders(entry.request.headers);
  const resHeaders = normalizeHeaders(entry.response.headers);
  const contentType = pickContentType(resHeaders);
  const body = formatBody(entry.response.content?.text, contentType);

  return {
    request: {
      method: entry.request.method,
      url: entry.request.url,
      headers: reqHeaders,
    },
    response: {
      status: entry.response.status,
      statusText: entry.response.statusText || '',
      headers: resHeaders,
      body,
      contentType,
    },
    timings: {
      total: Math.round(entry.time),
      // The current SDK doesn't break down DNS / connect / send / wait /
      // receive — only `total` is populated. Other fields stay undefined and
      // the UI hides rows when they're missing.
    },
  };
};

/**
 * HAR spec stores headers as `[{name, value}, ...]`. The Zight SDK currently
 * emits them as a record. We accept both so a future SDK alignment doesn't
 * break us.
 */
export const normalizeHeaders = (headers: RawHarHeaders | undefined): Record<string, string> => {
  if (!headers) return {};
  if (Array.isArray(headers)) {
    return headers.reduce<Record<string, string>>((acc, h) => {
      if (h?.name) {
        acc[h.name] = h.value ?? '';
      }
      return acc;
    }, {});
  }
  return headers;
};

const pickContentType = (headers: Record<string, string>): string | undefined => {
  // Header keys are case-insensitive; check both common spellings.
  return headers['content-type'] ?? headers['Content-Type'];
};

/**
 * Pre-format the response body so the detail panel renders cheaply.
 * For JSON content-types we attempt to parse and pretty-print; on failure
 * we fall back to the raw string. For non-JSON we pass through.
 */
export const formatBody = (raw: string | undefined, contentType: string | undefined): string | undefined => {
  if (!raw) return undefined;
  if (contentType?.includes('application/json')) {
    try {
      return JSON.stringify(JSON.parse(raw), null, 2);
    } catch {
      return raw;
    }
  }
  return raw;
};

// ---------------------------------------------------------------------------
// Browser info
// ---------------------------------------------------------------------------

export const normalizeBrowserInfo = (raw: RawBrowserInfoEntry): BrowserInfo => ({
  userAgent: raw.userAgent ?? '',
  platform: raw.platform ?? '',
  language: raw.language ?? '',
  screenResolution: raw.screen ? `${raw.screen.width} × ${raw.screen.height}` : '',
  viewportSize: raw.viewport ? `${raw.viewport.width} × ${raw.viewport.height}` : '',
  timezone: raw.timezone ?? '',
});

// ---------------------------------------------------------------------------
// Time anchor utility
// ---------------------------------------------------------------------------

/**
 * Convert an absolute epoch ms timestamp into seconds-from-anchor, clamped
 * to 0 so clock-skew artifacts don't render as negative timestamps.
 */
const relativeSeconds = (epochMs: number, anchorMs: number): number => {
  return Math.max(0, (epochMs - anchorMs) / 1000);
};
